import React, { useMemo, useState } from 'react';
import {
  ANALYTICS_COLORS,
  ANALYTICS_MESSAGES,
  ANALYTICS_USER_SORT_OPTIONS,
  CONTRIBUTION_BAR_MIN_DENOMINATOR,
  INITIAL_RANK_INDEX,
  LEADERBOARD_LIMIT,
  LEADERBOARD_RANK_DIGITS,
  PERCENT_MAX,
  USER_SORT_KEYS,
} from '../../../constants/analytics.constants';
import { AnalyticsUserSortKey, UserContribution } from '../../../types/analytics.types';
import { getInitials } from '../../../utils/analytics.utils';

interface AnalyticsUserLeaderboardProps {
  users: UserContribution[];
  isLoading: boolean;
}

const EMPTY_LIST_LENGTH = 0;
const RANK_PAD_CHAR = '0';

const ContributionBar: React.FC<{
  created: number;
  updated: number;
  max: number;
}> = ({ created, updated, max }) => {
  const safeMax = Math.max(max, CONTRIBUTION_BAR_MIN_DENOMINATOR);
  const createdPct = (created / safeMax) * PERCENT_MAX;
  const updatedPct = (updated / safeMax) * PERCENT_MAX;
  return (
    <div className="flex h-1 w-full overflow-hidden rounded-full bg-slate-100">
      <div className="transition-all" style={{ width: `${createdPct}%`, backgroundColor: ANALYTICS_COLORS.CREATED }} />
      <div className="transition-all" style={{ width: `${updatedPct}%`, backgroundColor: ANALYTICS_COLORS.UPDATED }} />
    </div>
  );
};

const CompactMetric: React.FC<{
  label: string;
  value: number;
  color: string;
  highlight: boolean;
}> = ({ label, value, color, highlight }) => (
  <span className={`inline-flex items-center gap-1 ${highlight ? '' : 'opacity-60'}`} title={`${label}: ${value}`}>
    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
    <span className="text-[11px] font-semibold tabular-nums text-slate-700">{value}</span>
  </span>
);

export const AnalyticsUserLeaderboard: React.FC<AnalyticsUserLeaderboardProps> = ({
  users,
  isLoading,
}) => {
  const [sortKey, setSortKey] = useState<AnalyticsUserSortKey>(USER_SORT_KEYS.TOTAL);

  const sortedUsers = useMemo(() => {
    const copy = [...users];
    copy.sort((a, b) => b[sortKey] - a[sortKey] || b.total - a.total);
    return copy.slice(EMPTY_LIST_LENGTH, LEADERBOARD_LIMIT);
  }, [users, sortKey]);

  const maxValue = useMemo(
    () => sortedUsers.reduce((acc, u) => Math.max(acc, u.total), EMPTY_LIST_LENGTH),
    [sortedUsers],
  );

  const isEmptyUsers = users.length === EMPTY_LIST_LENGTH;
  const isEmptySorted = sortedUsers.length === EMPTY_LIST_LENGTH;

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm h-full flex flex-col">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-900 whitespace-nowrap">Top contributors</h3>
        <div className="inline-flex rounded-lg bg-slate-50 border border-slate-200 p-0.5 flex-shrink-0">
          {ANALYTICS_USER_SORT_OPTIONS.map((opt) => {
            const isActive = sortKey === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSortKey(opt.value)}
                className={`px-2 py-0.5 text-[10px] font-semibold rounded-md transition-colors ${
                  isActive ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {isLoading && isEmptyUsers ? (
        <div className="p-8 text-center text-xs text-slate-400">Loading contributors…</div>
      ) : isEmptySorted ? (
        <div className="p-8 text-center text-xs text-slate-400">{ANALYTICS_MESSAGES.EMPTY_USERS}</div>
      ) : (
        <ul className="divide-y divide-slate-100 flex-1 overflow-auto">
          {sortedUsers.map((user, index) => (
            <li key={user.userId} className="px-3 py-2 flex items-center gap-2.5">
              <span className="text-[10px] font-bold text-slate-300 tabular-nums w-4 flex-shrink-0 text-center">
                {String(index + INITIAL_RANK_INDEX).padStart(LEADERBOARD_RANK_DIGITS, RANK_PAD_CHAR)}
              </span>
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt=""
                  className="w-7 h-7 rounded-full border border-slate-200 flex-shrink-0"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold flex items-center justify-center border border-slate-200 flex-shrink-0">
                  {getInitials(user.name)}
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-semibold text-slate-900 truncate">
                    {user.name || user.email}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <CompactMetric
                      label="Created"
                      value={user.created}
                      color={ANALYTICS_COLORS.CREATED}
                      highlight={sortKey !== USER_SORT_KEYS.UPDATED}
                    />
                    <CompactMetric
                      label="Updated"
                      value={user.updated}
                      color={ANALYTICS_COLORS.UPDATED}
                      highlight={sortKey !== USER_SORT_KEYS.CREATED}
                    />
                    <span className="text-sm font-semibold text-slate-900 tabular-nums border-l border-slate-200 ml-1 pl-2">
                      {user.total}
                    </span>
                  </div>
                </div>
                <div className="mt-1.5">
                  <ContributionBar created={user.created} updated={user.updated} max={maxValue} />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
